/**
 * Knack contact section enhancements.
 *
 * 1. Support tiles: tiles that carry a routing reason are real anchors to the form (work with zero
 *    JS). When JS runs, clicking one also preselects the matching "reason for contact" option,
 *    smooth-scrolls to the form (unless reduced motion) and moves focus to the reason control.
 *
 * 2. Klaviyo on submit (optional, configured via data-* on the form card): on submit, fires a Klaviyo
 *    client-side event and, when the visitor ticks the marketing-consent box, subscribes their email
 *    to the configured list. Uses fetch keepalive so the beacons complete as the native Shopify
 *    contact form posts. Never blocks or delays the actual submission; failures are swallowed.
 *    Only the public company ID and list ID are used (both non-secret, browser-safe).
 */
(() => {
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ── 1. Tiles ── */
  const tiles = document.querySelectorAll('[data-knack-reason][data-knack-target]');
  for (const tile of tiles) {
    tile.addEventListener('click', (event) => {
      const reason = tile.getAttribute('data-knack-reason');
      const select = document.getElementById(tile.getAttribute('data-knack-target'));
      if (!select) return;

      const match = Array.from(select.options).find(
        (opt) => opt.value === reason || opt.text.trim().toLowerCase() === reason.trim().toLowerCase()
      );
      if (match) {
        select.value = match.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }

      const form = document.getElementById('knack-contact-form');
      if (form) {
        event.preventDefault();
        form.scrollIntoView({ behavior: prefersReduced ? 'auto' : 'smooth', block: 'start' });
        window.setTimeout(() => select.focus({ preventScroll: true }), prefersReduced ? 0 : 400);
      }
    });
  }

  /* ── 2. Klaviyo on submit ── */
  const KLAVIYO_REVISION = '2024-10-15';

  const cards = document.querySelectorAll('.knack-contact__form-card[data-klaviyo-company]');
  for (const card of cards) {
    const company = card.getAttribute('data-klaviyo-company');
    const listId = card.getAttribute('data-klaviyo-list');
    const metric = card.getAttribute('data-klaviyo-metric') || 'Contacted Knack';
    const form = card.querySelector('form');
    if (!company || !form) continue;

    const value = (name) => {
      const el = form.querySelector(`[name="contact[${name}]"]`);
      return el ? el.value.trim() : '';
    };

    form.addEventListener('submit', () => {
      try {
        const email = value('email');
        if (!email) return; // The native required attribute blocks empty submits anyway.

        const consentEl = form.querySelector('[name="contact[Marketing consent]"]');
        const consented = !!(consentEl && consentEl.checked);

        const headers = { 'Content-Type': 'application/json', revision: KLAVIYO_REVISION };

        // Event: track every contact (helps segmentation + flows), regardless of consent.
        const event = {
          data: {
            type: 'event',
            attributes: {
              metric: { data: { type: 'metric', attributes: { name: metric } } },
              profile: { data: { type: 'profile', attributes: { email } } },
              properties: {
                reason: value('Reason'),
                order_number: value('Order number'),
                source: 'Contact page',
              },
              time: new Date().toISOString(),
            },
          },
        };
        fetch(`https://a.klaviyo.com/client/events/?company_id=${encodeURIComponent(company)}`, {
          method: 'POST',
          headers,
          keepalive: true,
          body: JSON.stringify(event),
        }).catch(() => {});

        // Subscription: only with explicit consent and a configured list.
        if (consented && listId) {
          const subscription = {
            data: {
              type: 'subscription',
              attributes: {
                custom_source: 'Contact page',
                // No `subscriptions` object. Klaviyo's client endpoint rejects it with
                // 400 "'subscriptions' is not a valid field for the resource 'profile'" - the
                // endpoint IS the subscribe action, so email alone is the whole payload. This
                // shipped wrong behind the silent .catch() below, so every consenting contact
                // since it launched failed to subscribe with no visible symptom. Confirmed by
                // testing both shapes against the live API: 400 with it, 202 without.
                profile: {
                  data: {
                    type: 'profile',
                    attributes: { email },
                  },
                },
              },
              relationships: { list: { data: { type: 'list', id: listId } } },
            },
          };
          fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(company)}`, {
            method: 'POST',
            headers,
            keepalive: true,
            body: JSON.stringify(subscription),
          })
            .then((res) => {
              // A bare catch is how the broken payload above stayed invisible. Leave a trace.
              if (!res.ok) console.error('[knack] Klaviyo subscription failed', res.status);
            })
            .catch(() => {});
        }
      } catch (err) {
        /* Never block the native submission. */
      }
    });
  }
})();
